// Geçmiş sekmesi — rut planı program satırlarının (RoutePlanInterval) versiyon geçmişi.
//
// Arka plan: satırlar artık silinmiyor, versiyonlanıyor. Bir düzenleme eski satırı kapatır
// (ValidTo = o an) ve yenisini açar (ValidTo = null). Backend ham versiyon listesini döner:
//   GET /api/route-plans/{id}/interval-history
//   → [{ customerId, customerTitle, weekNo, dayNo, visitOrderNo, validFrom, validTo, changedBy }]
//   validTo == null  → yürürlükteki satır
//
// Bu ekran ham satırları kullanıcıya anlamlı bir DEĞİŞİKLİK GÜNLÜĞÜ olarak sunar:
//   1) Aynı anda (aynı ValidFrom/ValidTo damgasında) olan satırlar bir "revizyon"da toplanır.
//   2) Revizyon içinde aynı müşterinin KAPANAN satırı ile AÇILAN satırı eşleştirilir →
//      "2. hafta · Salı → 2. hafta · Çarşamba" gibi bir geçiş olarak gösterilir.
//   3) Eşleşmeyen açılan satır "eklendi", eşleşmeyen kapanan satır "çıkarıldı" olur.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { authHeaders } from '../listPageStyles';
import { loadAuditUserMap, resolveAuditUser } from '../detail/auditUsers';
import { API_BASE, pick, unwrap, dayLabel, fmtDateTime } from './routePlanUtils';

// Aynı işlemde yazılan satırların zaman damgaları birkaç milisaniye kayabilir; bu pencere
// içindeki damgalar TEK revizyon sayılır.
const REVISION_TOLERANCE_MS = 2000;

// ── Ham satır okuyucular (backend PascalCase de dönebilir) ────────────────────
function readRow(r) {
  const validFrom = pick(r, 'validFrom', 'ValidFrom') ?? null;
  const validTo = pick(r, 'validTo', 'ValidTo') ?? null;
  return {
    customerId: pick(r, 'customerId', 'CustomerId') ?? null,
    customerTitle: pick(r, 'customerTitle', 'CustomerTitle') || '(müşteri adı yok)',
    weekNo: Number(pick(r, 'weekNo', 'WeekNo') ?? 0) || 0,
    dayNo: Number(pick(r, 'dayNo', 'DayNo') ?? 0) || 0,
    visitOrderNo: Number(pick(r, 'visitOrderNo', 'VisitOrderNo') ?? 0) || 0,
    changedBy: pick(r, 'changedBy', 'ChangedBy') ?? null,
    validFrom,
    validTo,
    fromTs: validFrom ? Date.parse(validFrom) : NaN,
    toTs: validTo ? Date.parse(validTo) : NaN,
    isCurrent: validTo === null || validTo === undefined,
  };
}

// "2. hafta · Çarşamba"
function slotLabel(row) {
  const week = row.weekNo ? `${row.weekNo}. hafta` : 'hafta yok';
  const day = row.dayNo ? dayLabel(row.dayNo) : 'gün yok';
  return `${week} · ${day}`;
}

// Zaman damgalarını REVISION_TOLERANCE_MS penceresiyle kümeler; her damga için küme
// temsilcisini (kümenin en küçük zamanı) döndüren bir çözümleyici üretir.
function buildRevisionClusters(times) {
  const sorted = [...new Set(times.filter((t) => Number.isFinite(t)))].sort((a, b) => a - b);
  const anchors = [];
  sorted.forEach((t) => {
    const last = anchors[anchors.length - 1];
    if (last === undefined || t - last > REVISION_TOLERANCE_MS) anchors.push(t);
  });
  const resolve = (t) => {
    if (!Number.isFinite(t)) return null;
    let anchor = null;
    for (const a of anchors) {
      if (t >= a && t - a <= REVISION_TOLERANCE_MS) anchor = a;
      else if (a > t) break;
    }
    return anchor;
  };
  return { anchors, resolve };
}

// Bir revizyondaki (kapanan, açılan) satırlardan müşteri bazlı değişim kalemleri üretir.
// Eşleştirme: önce aynı hafta+gün (yalnız ziyaret sırası değişmiş) çiftleri, sonra kalanlar
// sırayla eşleşir (taşındı). Artan açılan → eklendi, artan kapanan → çıkarıldı.
function diffRevision(closedRows, openedRows) {
  const byCustomer = new Map();
  const bucket = (r) => {
    const key = r.customerId ?? r.customerTitle;
    if (!byCustomer.has(key)) byCustomer.set(key, { title: r.customerTitle, closed: [], opened: [] });
    return byCustomer.get(key);
  };
  closedRows.forEach((r) => bucket(r).closed.push(r));
  openedRows.forEach((r) => bucket(r).opened.push(r));

  const items = [];
  const slotSort = (a, b) => (a.weekNo - b.weekNo) || (a.dayNo - b.dayNo) || (a.visitOrderNo - b.visitOrderNo);

  byCustomer.forEach((entry) => {
    const closed = [...entry.closed].sort(slotSort);
    const opened = [...entry.opened].sort(slotSort);

    // 1) Aynı hafta+gün çiftleri: konum korunmuş, olsa olsa ziyaret sırası değişmiş.
    for (let i = closed.length - 1; i >= 0; i -= 1) {
      const match = opened.findIndex((o) => o.weekNo === closed[i].weekNo && o.dayNo === closed[i].dayNo);
      if (match === -1) continue;
      const from = closed[i];
      const to = opened[match];
      closed.splice(i, 1);
      opened.splice(match, 1);
      if (from.visitOrderNo !== to.visitOrderNo) {
        items.push({
          kind: 'reorder', customer: entry.title, from, to,
          detail: `${slotLabel(to)} · ziyaret sırası ${from.visitOrderNo || '—'} → ${to.visitOrderNo || '—'}`,
        });
      }
      // Sıra da aynıysa görünür bir değişim yok → kalem üretme.
    }

    // 2) Kalan kapanan ↔ kalan açılan: taşınma.
    while (closed.length && opened.length) {
      const from = closed.shift();
      const to = opened.shift();
      items.push({ kind: 'move', customer: entry.title, from, to });
    }

    // 3) Artanlar.
    opened.forEach((to) => items.push({ kind: 'add', customer: entry.title, to }));
    closed.forEach((from) => items.push({ kind: 'remove', customer: entry.title, from }));
  });

  const KIND_ORDER = { move: 0, reorder: 1, add: 2, remove: 3 };
  items.sort((a, b) => (KIND_ORDER[a.kind] - KIND_ORDER[b.kind])
    || a.customer.localeCompare(b.customer, 'tr'));
  return items;
}

export default function HistoryTab({ planId, showToast }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userMap, setUserMap] = useState(null);
  const [expanded, setExpanded] = useState({});

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/${planId}/interval-history`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Değişiklik geçmişi alınamadı (${res.status})`);
      const data = unwrap(await res.json());
      setRows(Array.isArray(data) ? data.map(readRow) : []);
    } catch (e) {
      // Uç henüz yayında olmayabilir → sekme çökmez, bilgilendirici kart gösterilir.
      const msg = e.message || 'Değişiklik geçmişi alınamadı';
      setRows([]);
      setError(msg);
      showToast?.('error', msg);
    } finally {
      setLoading(false);
    }
  }, [planId, showToast]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  useEffect(() => { loadAuditUserMap().then(setUserMap).catch(() => setUserMap({})); }, []);

  // Ham satırlar → revizyonlar (yeniden eskiye).
  const revisions = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    const { anchors, resolve } = buildRevisionClusters([
      ...rows.map((r) => r.fromTs),
      ...rows.map((r) => r.toTs),
    ]);

    const opened = new Map();
    const closed = new Map();
    anchors.forEach((a) => { opened.set(a, []); closed.set(a, []); });
    rows.forEach((r) => {
      const f = resolve(r.fromTs);
      if (f !== null) opened.get(f).push(r);
      const t = resolve(r.toTs);
      if (t !== null) closed.get(t).push(r);
    });

    return anchors
      .map((anchor) => {
        const openedRows = opened.get(anchor) || [];
        const closedRows = closed.get(anchor) || [];
        const items = diffRevision(closedRows, openedRows);
        // Kim: açılan satırların changedBy'ı önceliklidir (revizyonu yapan kişi).
        const who = openedRows.find((r) => r.changedBy)?.changedBy
          ?? closedRows.find((r) => r.changedBy)?.changedBy
          ?? null;
        return {
          anchor,
          at: openedRows[0]?.validFrom ?? closedRows[0]?.validTo ?? new Date(anchor).toISOString(),
          who,
          items,
          isCurrent: openedRows.some((r) => r.isCurrent),
          isInitial: closedRows.length === 0 && openedRows.length > 0,
        };
      })
      .filter((rev) => rev.items.length > 0)
      .sort((a, b) => b.anchor - a.anchor);
  }, [rows]);

  const currentCount = useMemo(() => (rows || []).filter((r) => r.isCurrent).length, [rows]);

  const toggle = (anchor) => setExpanded((e) => ({ ...e, [anchor]: !e[anchor] }));

  if (loading) {
    return <div style={card}><div style={{ padding: 24, color: 'var(--text-secondary,#6b7280)' }}>Yükleniyor…</div></div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 800 }}>Program Değişiklik Geçmişi</h3>
            <p style={{ fontSize: 12.5, color: 'var(--text-secondary,#6b7280)', margin: 0 }}>
              Bu rut planındaki müşteri × hafta × gün atamalarının zaman içindeki değişimi.
              Diğer sekmeler her zaman yürürlükteki planı gösterir.
            </p>
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {!error && (
              <span style={metaChip}>
                {revisions.length} revizyon · {currentCount} yürürlükteki satır
              </span>
            )}
            <button style={{ ...ghostBtn, marginTop: 0 }} onClick={fetchHistory}>⟳ Yenile</button>
          </div>
        </div>

        {error && (
          <div style={errorBox}>
            <strong style={{ display: 'block', marginBottom: 3 }}>Geçmiş şu an alınamıyor</strong>
            <span style={{ fontSize: 12.5 }}>{error}</span>
          </div>
        )}

        {!error && revisions.length === 0 && (
          <div style={emptyBox}>Bu planda henüz değişiklik yok.</div>
        )}
      </div>

      {revisions.map((rev) => {
        const isOpen = !!expanded[rev.anchor];
        const visible = isOpen ? rev.items : rev.items.slice(0, COLLAPSE_LIMIT);
        const hidden = rev.items.length - visible.length;
        return (
          <div key={rev.anchor} style={card}>
            <div style={revHead}>
              <span style={{ ...dot, background: rev.isCurrent ? 'var(--success)' : '#94a3b8' }} aria-hidden="true" />
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 14.5, fontWeight: 800 }}>{fmtDateTime(rev.at)}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary,#6b7280)', marginTop: 2 }}>
                  {resolveAuditUser(rev.who, userMap)} · {rev.items.length} değişiklik
                </div>
              </div>
              <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                {rev.isInitial && <span style={{ ...badge, ...badgeNeutral }}>İlk program</span>}
                {rev.isCurrent && <span style={{ ...badge, ...badgeOk }}>Yürürlükte</span>}
              </div>
            </div>

            <ul style={itemList}>
              {visible.map((it, i) => (
                <li key={`${rev.anchor}-${i}`} style={itemRow}>
                  <span style={{ ...kindChip, ...KIND_STYLE[it.kind] }}>
                    {KIND_LABEL[it.kind]}
                  </span>
                  <span style={{ fontWeight: 700, fontSize: 13.5 }}>{it.customer}</span>
                  <span style={itemDetail}>
                    {it.kind === 'move' && (
                      <>
                        <span style={oldSlot}>{slotLabel(it.from)}</span>
                        <span style={{ margin: '0 6px', color: 'var(--text-secondary,#9ca3af)' }}>→</span>
                        <span style={newSlot}>{slotLabel(it.to)}</span>
                      </>
                    )}
                    {it.kind === 'reorder' && it.detail}
                    {it.kind === 'add' && <span style={newSlot}>{slotLabel(it.to)}</span>}
                    {it.kind === 'remove' && <span style={oldSlot}>{slotLabel(it.from)}</span>}
                  </span>
                </li>
              ))}
            </ul>

            {hidden > 0 && (
              <button style={ghostBtn} onClick={() => toggle(rev.anchor)}>+ {hidden} değişiklik daha</button>
            )}
            {isOpen && rev.items.length > COLLAPSE_LIMIT && (
              <button style={ghostBtn} onClick={() => toggle(rev.anchor)}>Daralt</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

const COLLAPSE_LIMIT = 6;

const KIND_LABEL = {
  move: '↔ taşındı',
  reorder: '↕ sıra',
  add: '+ eklendi',
  remove: '− çıkarıldı',
};

const KIND_STYLE = {
  move: { color: '#1d4ed8', background: 'rgba(0,115,234,0.12)', borderColor: 'rgba(0,115,234,0.35)' },
  reorder: { color: '#7c3aed', background: 'rgba(122,60,255,0.12)', borderColor: 'rgba(122,60,255,0.35)' },
  add: { color: '#15803d', background: 'rgba(0,200,117,0.12)', borderColor: 'rgba(0,200,117,0.35)' },
  remove: { color: '#b91c1c', background: 'rgba(226,68,92,0.12)', borderColor: 'rgba(226,68,92,0.35)' },
};

const card = {
  background: 'var(--bg-card,#fff)', border: '1px solid var(--border-color,#e5e7eb)',
  borderRadius: 18, padding: 22, boxShadow: 'var(--shadow-card, 0 12px 28px rgba(15,23,42,0.06))',
};
const revHead = {
  display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
  paddingBottom: 14, borderBottom: '1px solid var(--border-color,#eef2f7)',
};
const dot = { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 };
const itemList = { listStyle: 'none', margin: '12px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 };
const itemRow = {
  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
  padding: '7px 10px', borderRadius: 10, background: 'rgba(148,163,184,0.06)',
};
const itemDetail = { fontSize: 12.5, color: 'var(--text-secondary,#6b7280)', marginLeft: 'auto', whiteSpace: 'nowrap' };
const oldSlot = { textDecoration: 'line-through', color: 'var(--text-secondary,#9ca3af)' };
const newSlot = { fontWeight: 700, color: 'var(--text-primary,#0f172a)' };
const kindChip = {
  fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 999,
  border: '1px solid', whiteSpace: 'nowrap',
};
const badge = { fontSize: 11.5, fontWeight: 800, padding: '3px 10px', borderRadius: 999, border: '1px solid', whiteSpace: 'nowrap' };
const badgeOk = { color: '#15803d', background: 'rgba(0,200,117,0.14)', borderColor: 'rgba(0,200,117,0.4)' };
const badgeNeutral = { color: '#475569', background: 'rgba(148,163,184,0.14)', borderColor: 'rgba(148,163,184,0.4)' };
const metaChip = {
  fontSize: 12, fontWeight: 700, color: 'var(--text-secondary,#475569)',
  background: 'rgba(148,163,184,0.12)', padding: '5px 10px', borderRadius: 999, whiteSpace: 'nowrap',
};
const ghostBtn = {
  marginTop: 12, padding: '6px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
  border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 8,
  background: 'var(--bg-card,#fff)', color: 'var(--text-primary,#0f172a)',
};
const errorBox = {
  marginTop: 16, padding: '12px 14px', borderRadius: 12, color: '#92600a',
  background: 'rgba(253,171,61,0.12)', border: '1px solid rgba(253,171,61,0.35)',
};
const emptyBox = {
  marginTop: 16, padding: '28px 14px', textAlign: 'center', borderRadius: 12,
  color: 'var(--text-secondary,#6b7280)', background: 'rgba(148,163,184,0.06)',
  border: '1px dashed var(--border-color,#e5e7eb)',
};
